<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { useMessageContext } from '../../provider.js';
import { formatKaptionNoteCardTimestamp } from 'dashboard/helper/kaptionNoteCards';
import { useKaptionNoteCardActions } from 'dashboard/composables/useKaptionNoteCardActions';

const props = defineProps({
  noteCard: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const { conversationId, id } = useMessageContext();
const { openDashboardApp } = useKaptionNoteCardActions();

const isSubmitting = ref(false);

const isPending = computed(() => props.noteCard.state === 'pending');

const cardContainerClass = computed(() => {
  if (isPending.value) {
    return 'rounded-xl bg-kpt-amber-light dark:bg-kpt-amber-soft px-4 py-4 shadow-[0_8px_20px_rgba(251,191,36,0.18)] dark:shadow-[0_8px_20px_rgba(251,191,36,0.08)]';
  }

  return 'rounded-xl bg-kpt-light-100 dark:bg-kpt-dark-700 px-4 py-4';
});

const headerIconClass = computed(() => {
  if (isPending.value) {
    return 'bg-kpt-light-50 dark:bg-kpt-dark-800 text-kpt-amber';
  }

  return 'bg-kpt-light-200 dark:bg-kpt-dark-600 text-kpt-text-secondary-light dark:text-kpt-text-secondary-dark';
});

const headerTextClass = computed(() => {
  return isPending.value
    ? 'text-kpt-amber'
    : 'text-kpt-text-secondary-light dark:text-kpt-text-secondary-dark';
});

const noteCardLabel = computed(() => {
  return props.noteCard.type === 'action'
    ? t('CONVERSATION.NOTE_CARDS.ACTION.TYPE_LABEL')
    : t('CONVERSATION.NOTE_CARDS.CONFIRMATION.TYPE_LABEL');
});

const resolutionTitle = computed(() => {
  if (props.noteCard.state === 'completed') {
    return `${t('CONVERSATION.NOTE_CARDS.ACTION.RESOLVED')}: ${props.noteCard.resolution.label}`;
  }

  if (props.noteCard.state === 'confirmed') {
    return `${t('CONVERSATION.NOTE_CARDS.CONFIRMATION.CONFIRMED')}: ${props.noteCard.resolution.label}`;
  }

  if (props.noteCard.state === 'cancelled') {
    return `${t('CONVERSATION.NOTE_CARDS.CONFIRMATION.CANCELLED')}: ${props.noteCard.resolution.label}`;
  }

  return '';
});

const resolutionMeta = computed(() => {
  const formattedTimestamp = formatKaptionNoteCardTimestamp(
    props.noteCard.resolution.at
  );
  if (!formattedTimestamp) return props.noteCard.resolution.by;
  return `${props.noteCard.resolution.by} · ${formattedTimestamp}`;
});

const resolutionPanelClass = computed(() => {
  if (props.noteCard.state === 'cancelled') {
    return 'rounded-lg bg-kpt-rose-light dark:bg-kpt-rose-soft px-3 py-3';
  }

  return 'rounded-lg bg-kpt-emerald-light dark:bg-kpt-emerald-soft px-3 py-3';
});

const resolutionTitleClass = computed(() => {
  return props.noteCard.state === 'cancelled'
    ? 'text-kpt-rose'
    : 'text-kpt-emerald';
});

const buttonActions = computed(() => {
  if (props.noteCard.type === 'action') {
    return [{ key: 'action', label: props.noteCard.buttons.action }];
  }

  return [
    { key: 'confirm', label: props.noteCard.buttons.confirm },
    { key: 'cancel', label: props.noteCard.buttons.cancel },
  ];
});

const buttonClass = actionKey => {
  const baseClass =
    'inline-flex min-h-10 items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

  if (actionKey === 'cancel') {
    return `${baseClass} bg-kpt-light-50 dark:bg-kpt-dark-800 text-kpt-text-primary-light dark:text-kpt-text-primary-dark hover:bg-kpt-amber-soft dark:hover:bg-kpt-dark-600 focus-visible:outline-kpt-amber`;
  }

  return `${baseClass} bg-kpt-amber text-kpt-text-primary-light hover:opacity-90 focus-visible:outline-kpt-amber`;
};

const resolveNoteCard = async actionName => {
  if (!isPending.value || isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    // If card has app_id, delegate resolution to the Dashboard App.
    // The app will call back to resolve the card after the action completes.
    if (props.noteCard.meta?.app_id) {
      openDashboardApp(props.noteCard.meta.app_id, {
        cardId: props.noteCard.meta.card_id,
        action: actionName,
        conversationId: conversationId.value,
        data: props.noteCard.data,
      });
      return;
    }

    const { data } = await MessageApi.noteCardAction(
      conversationId.value,
      id.value,
      actionName
    );
    store.dispatch('updateMessage', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONVERSATION.NOTE_CARDS.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    data-test="note-card-container"
    class="flex flex-col gap-3"
    :class="cardContainerClass"
  >
    <div
      class="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em]"
    >
      <span
        class="inline-flex items-center justify-center w-5 h-5 rounded-full"
        :class="headerIconClass"
      >
        <fluent-icon
          size="12"
          :icon="noteCard.type === 'action' ? 'info' : 'checkmark-circle'"
        />
      </span>
      <span class="font-semibold" :class="headerTextClass">
        {{ noteCardLabel }}
      </span>
    </div>

    <div class="space-y-1">
      <p
        class="mb-0 text-sm font-semibold leading-5 text-kpt-text-primary-light dark:text-kpt-text-primary-dark"
      >
        {{ noteCard.title }}
      </p>
      <p
        class="mb-0 text-sm leading-5 text-kpt-text-secondary-light dark:text-kpt-text-secondary-dark"
      >
        {{ noteCard.subtitle }}
      </p>
    </div>

    <div
      v-if="isPending"
      class="flex flex-wrap items-center gap-2"
      :class="noteCard.type === 'action' ? 'justify-center' : 'justify-start'"
    >
      <button
        v-for="buttonAction in buttonActions"
        :key="buttonAction.key"
        :class="buttonClass(buttonAction.key)"
        type="button"
        :disabled="isSubmitting"
        @click="resolveNoteCard(buttonAction.key)"
      >
        {{ buttonAction.label }}
      </button>
    </div>

    <div v-else data-test="note-card-resolution" :class="resolutionPanelClass">
      <p class="mb-1 text-sm font-semibold" :class="resolutionTitleClass">
        {{ resolutionTitle }}
      </p>
      <p
        class="mb-0 text-xs text-kpt-text-secondary-light dark:text-kpt-text-secondary-dark"
      >
        {{ resolutionMeta }}
      </p>
    </div>
  </div>
</template>
